import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { CenterView, RowView, RowView2 } from "../../../../Component/style/common_style";
import { fontStyles } from "../../../../Component/style/admin_font";

interface ProductImageModalProps {
  visible: boolean;
  onClose: () => void;
  images: string[];
  onSave: (updatedImages: string[]) => Promise<void>;
  captureImage?: () => Promise<File>;
}

const ProductImageModal = ({ visible, onClose, images, onSave, captureImage }: ProductImageModalProps) => {
  const [tempImages, setTempImages] = useState<string[]>(images);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const capturingRef = useRef(false);
  const captureRequest = useRef(0);
  const savingRef = useRef(false);
  const [error, setError] = useState('');
  const objectUrls = useRef<string[]>([]);


  useEffect(() => {
    if (visible) {
      setTempImages(images || []);
      setError('');
    }
  }, [visible, images]);

  useEffect(() => {
    if (!visible) {
      objectUrls.current.forEach(value => URL.revokeObjectURL(value));
      objectUrls.current = [];
    }
    return () => {
      captureRequest.current += 1;
      objectUrls.current.forEach(value => URL.revokeObjectURL(value));
      objectUrls.current = [];
    };
  }, [visible]);

  if (!visible) return null;

  const close = () => {
    if (!savingRef.current && !capturingRef.current) onClose();
  };

  const handleImageDelete = (index: number) => {
    if (savingRef.current || capturingRef.current) return;
    setTempImages(tempImages.filter((_, i) => i !== index));
  };

  const moveImage = (from: number, to: number) => {
    if (savingRef.current || capturingRef.current || from === to) return;
    setTempImages(current => {
      const reordered = [...current];
      const [image] = reordered.splice(from, 1);
      reordered.splice(to, 0, image);
      return reordered;
    });
  };

  const triggerFileInput = () => {
    if (savingRef.current || capturingRef.current || tempImages.length >= 10) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || savingRef.current || capturingRef.current) return;

    const remainingSlots = 10 - tempImages.length;
    if (files.length > remainingSlots) {
      setError(`상품 이미지는 최대 10장까지 등록할 수 있습니다. 지금은 ${remainingSlots}장 더 추가할 수 있습니다.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const filesToUpload = Array.from(files);
    if (filesToUpload.some(file => !['image/jpeg', 'image/png'].includes(file.type) || file.size > 10 * 1024 * 1024)) {
      setError('10MB 이하의 JPG 또는 PNG 이미지를 선택해주세요.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const newImageUrls = filesToUpload.map((file) => URL.createObjectURL(file));
    objectUrls.current.push(...newImageUrls);
    setTempImages([...tempImages, ...newImageUrls]);
    setError('');

    // reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCapture = async () => {
    if (!captureImage || savingRef.current || capturingRef.current || tempImages.length >= 10) return;
    const request = ++captureRequest.current;
    capturingRef.current = true;
    setCapturing(true);
    setError('');
    try {
      const file = await captureImage();
      if (request !== captureRequest.current) return;
      if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        throw new Error('캡처 이미지는 10MB 이하의 JPG 또는 PNG여야 합니다.');
      }
      const imageUrl = URL.createObjectURL(file);
      objectUrls.current.push(imageUrl);
      setTempImages(previous => [...previous, imageUrl]);
    } catch (failure) {
      if (request === captureRequest.current) {
        setError(failure instanceof Error ? failure.message : '방송 화면을 캡처하지 못했습니다.');
      }
    } finally {
      if (request === captureRequest.current) {
        capturingRef.current = false;
        setCapturing(false);
      }
    }
  };

  const handleSave = async () => {
    if (savingRef.current || capturingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      await onSave(tempImages);
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '이미지 저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const isLimitReached = tempImages.length >= 10;

  return (
    <ModalOverlay onClick={close}>
      <ModalContent role="dialog" aria-modal="true" aria-labelledby="product-image-title" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="title" id="product-image-title">상품 이미지 관리</div>
          <CloseButton tone="admin" type="button" aria-label="상품 이미지 관리 창 닫기" disabled={saving || capturing}
            onClick={close} />
        </ModalHeader>
        <HeaderDivider />
        <Guide>상품 사진은 최대 10장입니다. 순서를 바꾸거나 대표 이미지로 지정할 수 있습니다.</Guide>
        <ImageCount aria-live="polite">등록 이미지 <strong>{tempImages.length}/10장</strong></ImageCount>

        <ImagesListContainer>
          {tempImages.length === 0 ? (
            <EmptyState>등록된 이미지가 없습니다.<span>사진 파일을 추가하거나 방송 화면을 캡처하세요.</span></EmptyState>
          ) : (
            tempImages.map((imgUrl, idx) => (
              <ImageItem key={idx}>
                <Thumbnail src={imgUrl} alt={`상품 이미지 ${idx + 1}`} />
                <ImageName>{idx === 0 ? "대표 이미지" : `추가 이미지 ${idx + 1}`}</ImageName>
                <ImageActions>
                  {idx > 0 && <OrderButton type="button" disabled={saving || capturing} onClick={() => moveImage(idx, 0)}>대표로 지정</OrderButton>}
                  <OrderButton type="button" aria-label={`이미지 ${idx + 1} 앞으로 이동`} disabled={saving || capturing || idx === 0} onClick={() => moveImage(idx, idx - 1)}>↑</OrderButton>
                  <OrderButton type="button" aria-label={`이미지 ${idx + 1} 뒤로 이동`} disabled={saving || capturing || idx === tempImages.length - 1} onClick={() => moveImage(idx, idx + 1)}>↓</OrderButton>
                  <OrderButton type="button" aria-label={`이미지 ${idx + 1} 삭제`} disabled={saving || capturing} onClick={() => handleImageDelete(idx)}>삭제</OrderButton>
                </ImageActions>
              </ImageItem>
            ))
          )}
        </ImagesListContainer>
        {error && <p role="alert">{error}</p>}

        <FooterRow>
          <UploadActions>
            <AddImageButton as="button" disabled={saving || capturing || isLimitReached} onClick={triggerFileInput}>
              파일에서 추가
            </AddImageButton>
            {captureImage && <AddImageButton as="button" disabled={saving || capturing || isLimitReached} onClick={handleCapture}>
              {capturing ? '캡처 중...' : '방송 화면 캡처'}
            </AddImageButton>}
          </UploadActions>
          <SaveButton as="button" disabled={saving || capturing} onClick={handleSave}>
            {saving ? '저장 중...' : '이미지 저장'}
          </SaveButton>
        </FooterRow>

        <input
          type="file"
          ref={fileInputRef}
          aria-label="상품 이미지 파일"
          onChange={handleFileChange}
          accept="image/jpeg,image/png"
          multiple
          style={{ display: "none" }}
        />
      </ModalContent>
    </ModalOverlay>
  );
};

export default ProductImageModal;

const ModalOverlay = styled(CenterView)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1000;
`;

const ModalContent = styled.div`
  box-sizing: border-box;
  width: 90%;
  max-width: 32rem;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background-color: var(--admin-theme-surface, white);
  border-radius: 12px;
  padding: 1.8rem;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  p[role="alert"] { color: var(--admin-danger); font-size: 14px; }
`;

const ModalHeader = styled(CenterView)`
  min-height: 44px;
  .title { padding: 0 48px; }
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
  .title {
    font-size: 18px;
    ${fontStyles.Bold}
    color: var(--admin-ink);
    text-align: center;
  }
`;

const CloseButton = styled(ModalCloseButton)`position: absolute; right: 0; top: 50%; transform: translateY(-50%);`;

const HeaderDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--admin-border);
  margin-bottom: 1.2rem;
`;

const Guide = styled.p`
  margin: 0 0 0.6rem;
  color: var(--admin-muted);
  font-size: 14px;
  line-height: 1.5;
`;

const ImageCount = styled.div`
  margin-bottom: 0.8rem;
  color: var(--admin-muted);
  font-size: 14px;
  strong { color: var(--admin-ink); ${fontStyles.semiBold} }
`;

const ImagesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 26rem;
  min-height: 16rem;
  overflow-y: auto;
  margin-bottom: 1.8rem;
  padding-right: 0.2rem;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--admin-theme-raised, #e2e2e2);
    border-radius: 4px;
  }
`;

const EmptyState = styled(CenterView)`
  height: 16rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: var(--admin-ink);
  span { color: var(--admin-muted); }
  font-size: 14px;
  ${fontStyles.regular}
`;

const ImageItem = styled(RowView2)`
  justify-content: space-between;
  padding: 0.6rem 0rem;
  border-bottom: 1px solid var(--admin-theme-border, #f9f9f9);
  &:last-child {
    border-bottom: none;
  }
  @media (max-width: 480px) { flex-wrap: wrap; }
`;

const Thumbnail = styled.img`
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--admin-theme-border, #f0f0f0);
`;

const ImageName = styled.div`
  flex: 1;
  margin-left: 1rem;
  font-size: 14px;
  color: var(--admin-ink);
  ${fontStyles.semiBold}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImageActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
  @media (max-width: 480px) { width: 100%; margin-top: 8px; justify-content: flex-start; }
`;

const OrderButton = styled.button`
  min-height: 30px;
  padding: 4px 7px;
  border: 1px solid var(--admin-border);
  border-radius: 7px;
  background: var(--admin-theme-surface, #fff);
  color: var(--admin-ink);
  font: inherit;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  &:hover:not(:disabled) { border-color: var(--admin-theme-link, var(--admin-accent)); background: var(--admin-accent-soft); }
  &:disabled { opacity: .4; cursor: not-allowed; }
`;

const FooterRow = styled(RowView)`
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  width: 100%;
`;

const UploadActions = styled(RowView)`
  gap: 0.75rem;
  width: 100%;
  @media (max-width: 480px) { flex-direction: column; }
`;

const SaveButton = styled(CenterView)`
  width: 100%;
  height: 3.2rem;
  background: var(--admin-accent);
  color: white;
  border-radius: 8px;
  font-size: 15px;
  ${fontStyles.Bold}
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const AddImageButton = styled(CenterView)`
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  height: 3.2rem;
  border: 1px solid var(--admin-accent);
  color: var(--admin-theme-link, var(--admin-accent));
  background-color: var(--admin-theme-surface, white);
  border-radius: 8px;
  font-size: 15px;
  ${fontStyles.Bold}
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background-color: var(--admin-theme-accent-soft, #f7f4ff);
  }
  &:disabled {
    background-color: var(--admin-theme-raised, #F0F0F0);
    border: none;
    color: var(--admin-muted);
    cursor: not-allowed;
    &:hover {
      background-color: var(--admin-theme-raised, #F0F0F0);
    }
  }
`;
